/**
 * Google Drive logo — 2026 gradient redesign.
 *
 * Recreated from Google's published mark (as catalogued on Wikimedia Commons,
 * public-domain on shape-originality grounds; the trademark still belongs to
 * Google, so the icon should only be used to represent the Drive service).
 *
 * Each render generates unique gradient/mask IDs via useId so multiple
 * instances on the same page (e.g. the AppHeader status indicator and the
 * folder-picker modal title) don't collide.
 */
import { useId } from "react";

interface GoogleDriveLogoProps {
  className?: string;
}

export default function GoogleDriveLogo({ className }: GoogleDriveLogoProps) {
  // React.useId returns environment-dependent strings like ":r0:" or
  // "«r0»" — both contain characters that are unsafe inside SVG url(#…)
  // fragment references. Strip everything but alphanumerics so we get
  // a guaranteed-valid fragment id (e.g. "r0").
  const rawId = useId();
  const uid = `gdl-${rawId.replace(/[^a-zA-Z0-9]/g, "") || "x"}`;
  const maskId = `${uid}-mask`;
  const gradYellow = `${uid}-y`;
  const gradBlue = `${uid}-b`;
  const gradGreen = `${uid}-g`;

  // viewBox matches the source SVG's natural dimensions. The mask
  // region (12,18)-(180,172) is in the inner <g>'s local space; after
  // the matrix(4.8140532,…) scale + translate is applied, the icon
  // actually fills the full 800 × 741.3696 canvas.
  return (
    <svg
      viewBox="0 0 800 741.3696"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <mask
        id={maskId}
        width="168"
        height="154"
        x="12"
        y="18"
        maskUnits="userSpaceOnUse"
      >
        <path
          fill="#fff"
          d="M63.09 37c14.626-25.333 51.193-25.334 65.819 0l45.033 78c14.626 25.334-3.657 57.001-32.91 57.001H50.967c-29.253 0-47.536-31.667-32.91-57.001Z"
        />
      </mask>
      <g
        mask={`url(#${maskId})`}
        transform="matrix(4.8140532,0,0,4.8140532,-62.146701,-86.652356)"
      >
        <path
          fill={`url(#${gradYellow})`}
          d="M206.905 172.02h-91.888l-19.015-32.934 45.944-79.578Z"
        />
        <path
          fill={`url(#${gradBlue})`}
          d="M-14.919 172.006 50.04 59.494v.002L31.032 92.422h38.02L115 172.004l-129.918.001Z"
        />
        <path
          fill={`url(#${gradGreen})`}
          d="M96.007-20.085 141.954 59.5l-19.011 32.928H31.048Z"
        />
      </g>
      <defs>
        <linearGradient
          id={gradYellow}
          x1="193.6"
          x2="103.09"
          y1="165.6"
          y2="111.21"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset=".09" stopColor="#ffe921" />
          <stop offset="1" stopColor="#fec700" />
        </linearGradient>
        <linearGradient
          id={gradBlue}
          x1="114.4"
          x2="15.53"
          y1="181.61"
          y2="121.8"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset=".15" stopColor="#a9a8ff" />
          <stop offset=".33" stopColor="#6d97ff" />
          <stop offset=".48" stopColor="#3186ff" />
        </linearGradient>
        <linearGradient
          id={gradGreen}
          x1="128.88"
          x2="28.7"
          y1="37.88"
          y2="84.64"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset=".55" stopColor="#0ebc5f" />
          <stop offset=".85" stopColor="#78c9ff" />
        </linearGradient>
      </defs>
    </svg>
  );
}
